/**
 * avatars/avatar-builder/builder-legs.js
 * Xây dựng chân và giày (Legs & Shoes) dạng Classic hoặc Articulated cho BlockyAvatar
 */

import * as THREE from 'three';

export function buildShoesAndLegs(avatar, pelvis, dimensions, materials) {
  const { legH, legW, legD } = dimensions;
  const { skinMat, pantsMat, shoesMat, soleMat } = materials;

  const shoeH = 0.14;
  const toeExt = 0.07;
  avatar.soleMeshes = [];

  const buildShoe = (parent, topY) => {
    const shoeGeo = new THREE.BoxGeometry(legW, shoeH, legD + toeExt);
    const shoeMesh = new THREE.Mesh(shoeGeo, shoesMat);
    shoeMesh.position.set(0, topY - shoeH / 2, toeExt / 2);
    shoeMesh.castShadow = false;
    shoeMesh.receiveShadow = true;
    parent.add(shoeMesh);

    const soleGeo = new THREE.BoxGeometry(legW + 0.006, 0.035, legD + toeExt + 0.006);
    const soleMesh = new THREE.Mesh(soleGeo, soleMat);
    soleMesh.position.set(0, topY - shoeH + 0.0175, toeExt / 2);
    soleMesh.castShadow = false;
    soleMesh.receiveShadow = true;
    parent.add(soleMesh);
    avatar.soleMeshes.push(soleMesh);
    avatar.disposables.push(shoeGeo, soleGeo);
  };

  const pantsH = legH - shoeH;

  const buildClassicLeg = (isLeft) => {
    const legGroup = new THREE.Group();
    legGroup.name = isLeft ? 'LeftLeg_Classic' : 'RightLeg_Classic';
    const posX = isLeft ? -legW / 2 : legW / 2;
    legGroup.position.set(posX, 0, 0);

    if (avatar.config.features.hasShorts) {
      const shortsH = pantsH * 0.55;
      const kneeH = pantsH - shortsH;

      const shortsGeo = new THREE.BoxGeometry(legW, shortsH, legD);
      const shortsMesh = new THREE.Mesh(shortsGeo, pantsMat);
      shortsMesh.position.y = -shortsH / 2;
      shortsMesh.castShadow = false;
      shortsMesh.receiveShadow = true;
      legGroup.add(shortsMesh);

      const kneeGeo = new THREE.BoxGeometry(legW, kneeH, legD);
      const kneeMesh = new THREE.Mesh(kneeGeo, skinMat);
      kneeMesh.position.y = -shortsH - kneeH / 2;
      kneeMesh.castShadow = false;
      kneeMesh.receiveShadow = true;
      legGroup.add(kneeMesh);
      avatar.disposables.push(shortsGeo, kneeGeo);
    } else {
      const pantsGeo = new THREE.BoxGeometry(legW, pantsH, legD);
      const pantsMesh = new THREE.Mesh(pantsGeo, pantsMat);
      pantsMesh.position.y = -pantsH / 2;
      pantsMesh.castShadow = false;
      pantsMesh.receiveShadow = true;
      legGroup.add(pantsMesh);
      avatar.disposables.push(pantsGeo);
    }

    buildShoe(legGroup, -pantsH);
    return legGroup;
  };

  const buildArticulatedLeg = (isLeft) => {
    const legGroup = new THREE.Group();
    legGroup.name = isLeft ? 'LeftThigh' : 'RightThigh';
    const posX = isLeft ? -legW / 2 : legW / 2;
    legGroup.position.set(posX, 0, 0);

    const hasShorts = !!avatar.config.features.hasShorts;
    const thighH = pantsH * 0.52;
    const calfH = pantsH - thighH;

    // 1. Thigh (Đùi)
    const thighGeo = new THREE.BoxGeometry(legW, thighH, legD);
    const thighMesh = new THREE.Mesh(thighGeo, pantsMat);
    thighMesh.position.y = -thighH / 2;
    thighMesh.castShadow = false;
    thighMesh.receiveShadow = true;
    legGroup.add(thighMesh);

    // 2. Calf (Cẳng chân & Giày xoay quanh đầu gối)
    const calfGroup = new THREE.Group();
    calfGroup.name = isLeft ? 'LeftCalf' : 'RightCalf';
    calfGroup.position.set(0, -thighH, 0);
    legGroup.add(calfGroup);

    // KHỚP TRỤ BÁN NGUYỆT ĐẦU GỐI (Cylindrical Knee Hinge Plug)
    const kneeR = legD * 0.46;
    const kneeWidth = legW * 0.94;
    const kneePlugGeo = new THREE.CylinderGeometry(kneeR, kneeR, kneeWidth, 24);
    kneePlugGeo.rotateZ(Math.PI / 2); // Nằm ngang dọc trục X
    const kneePlugMat = hasShorts ? skinMat : pantsMat;
    const kneePlugMesh = new THREE.Mesh(kneePlugGeo, kneePlugMat);
    kneePlugMesh.position.set(0, 0, 0);
    kneePlugMesh.castShadow = false;
    kneePlugMesh.receiveShadow = true;
    calfGroup.add(kneePlugMesh);

    const calfMat = hasShorts ? skinMat : pantsMat;
    const calfGeo = new THREE.BoxGeometry(legW, calfH, legD);
    const calfMesh = new THREE.Mesh(calfGeo, calfMat);
    calfMesh.position.y = -calfH / 2;
    calfMesh.castShadow = false;
    calfMesh.receiveShadow = true;
    calfGroup.add(calfMesh);

    buildShoe(calfGroup, -calfH);

    avatar.disposables.push(thighGeo, kneePlugGeo, calfGeo);
    return { legGroup, calfGroup };
  };

  if (avatar.isArticulated) {
    const leftLegData = buildArticulatedLeg(true);
    leftLegData.legGroup.rotation.z = -0.018;
    pelvis.add(leftLegData.legGroup);
    avatar.parts.leftLeg = leftLegData.legGroup;
    avatar.parts.leftThigh = leftLegData.legGroup;
    avatar.parts.leftCalf = leftLegData.calfGroup;

    const rightLegData = buildArticulatedLeg(false);
    rightLegData.legGroup.rotation.z = 0.018;
    pelvis.add(rightLegData.legGroup);
    avatar.parts.rightLeg = rightLegData.legGroup;
    avatar.parts.rightThigh = rightLegData.legGroup;
    avatar.parts.rightCalf = rightLegData.calfGroup;
  } else {
    const leftLegGroup = buildClassicLeg(true);
    leftLegGroup.rotation.z = -0.018;
    pelvis.add(leftLegGroup);
    avatar.parts.leftLeg = leftLegGroup;

    const rightLegGroup = buildClassicLeg(false);
    rightLegGroup.rotation.z = 0.018;
    pelvis.add(rightLegGroup);
    avatar.parts.rightLeg = rightLegGroup;
  }
}
